import styles from './styles.module.scss';
import React from 'react';
import {get, startCase, capitalize} from 'lodash';
import {Link, useHistory} from 'react-router-dom';
import {isEmpty} from 'lodash/lang';
import classNames from 'classnames';

import {
  DropdownToggle,
  DropdownMenu,
  Dropdown,
  Badge,
  UncontrolledDropdown,
  DropdownItem,
  Button,
  UncontrolledTooltip,
} from 'reactstrap';
import {LabelDate} from 'components/Labels';
import {LoadingSpinner} from 'components/Loading';

import {
  NotificationProvider,
  NotificationConsumer,
} from 'contexts/NotificationContext';
import {ContentTitle} from 'components/Contents';
import {NOTIFICATION_CATEGORY_LABEL} from 'configs/notifications';
import {SUBSCRIPTION_PLANS_TYPE_LABEL} from 'configs/subscription';
import {ACTIVITY_RESULT_STATUS} from 'configs/data-management';
import dayjs from 'dayjs';
import {
  SUBSCRIPTION_TYPE_LABEL,
  SUBSCRIPTION_TYPE_LEVEL,
} from 'configs/membership';
import {useAuth} from 'contexts/AuthContext';
import {ACCOUNT_ROLES} from 'configs';
import {date} from 'utils/dates';
import MuiIcon from 'components/Icons/MuiIcon';

function NotificationDropItem(props) {
  const {onMarkRead, onDelete, notification} = props;
  const {notification_id, status} = notification || {};

  return (
    <UncontrolledDropdown direction="down">
      <DropdownToggle
        nav
        color="transparent"
        className="text-secondary d-flex align-items-center"
      >
        <MuiIcon name="more_vert" />
      </DropdownToggle>
      <DropdownMenu>
        {status === 'unread' && (
          <DropdownItem
            className="py-2 d-flex align-items-center"
            onClick={() => onMarkRead(notification_id)}
          >
            <MuiIcon name="check_circle" className="text-secondary mr-1" />
            <span style={{fontSize: '14px'}}>Mark as read</span>
          </DropdownItem>
        )}
        <DropdownItem
          className="py-2 d-flex align-items-center"
          onClick={() => onDelete(notification_id)}
        >
          <MuiIcon name="delete" className="text-secondary mr-1" />
          <span style={{fontSize: '14px'}}>Remove notification</span>
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}

function NotificationDropAll(props) {
  const {toggle, handleMarkAllAsRead, hasUnreadNotifications} = props;
  const history = useHistory();

  function handleClick() {
    if (toggle) toggle();
    history.push('/settings/notification');
  }

  return (
    <UncontrolledDropdown direction="down">
      <DropdownToggle
        nav
        color="transparent"
        className="text-secondary d-flex align-items-center"
      >
        <MuiIcon name="more_vert" />
      </DropdownToggle>
      <DropdownMenu>
        {hasUnreadNotifications && (
          <DropdownItem
            className="py-2 d-flex align-items-center"
            onClick={handleMarkAllAsRead}
          >
            <MuiIcon name="check_circle" className="text-secondary mr-1" />
            <span style={{fontSize: '14px'}}>Mark all as read</span>
          </DropdownItem>
        )}
        <DropdownItem
          className="py-2 d-flex align-items-center"
          onClick={handleClick}
        >
          <MuiIcon name="settings" className="text-secondary mr-1" />
          <span style={{fontSize: '14px'}}>Open Notifications Settings</span>
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}

function getNotificationProperties(code, details = {}, accountRole) {
  let message, link, iconColor, icon;
  const {
    is_cert_upload,
    error_count,
    skipped_cert_count,
    uploaded_cert_count,
    file_name,
    verification_type,
    plan_name,
    billing_date,
    plan,
  } = details;

  const isSuperAdmin = accountRole === ACCOUNT_ROLES.super_admin;
  const isAdminOrStaff = accountRole !== ACCOUNT_ROLES.super_admin;

  switch (code) {
    case 'near_verification_limit_version_2': {
      message = 'Your credits are running low!';
      icon = 'warning';
      iconColor = 'notification__icon--warning';
      link = isAdminOrStaff ? '/dashboard' : '/membership/overview';
      break;
    }
    case 'contact_request_sent_successfully': {
      message = 'Contact Request Sent Successfully!';
      icon = 'check_circle';
      iconColor = 'notification__icon--success';
      link = '/enquiries/subscription/status';
      break;
    }
    case 'monthly_overlimit_initial_payment_failed': {
      message = 'Payment for Overlimit Unsuccessful!';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = '/membership';
      break;
    }
    case 'monthly_overlimit_first_retry_payment_failed': {
      message =
        'Payment Unsuccessful - First Attempt: Update Payment Method Required';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = '/membership';
      break;
    }
    case 'monthly_overlimit_second_retry_payment_failed': {
      message =
        'Payment Unsuccessful - Second Attempt: Update Payment Method Required to Avoid Account Suspension';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = '/membership';
      break;
    }
    case 'monthly_overlimit_third_retry_payment_failed': {
      message = 'Account Suspended - Update Payment Method to restore access';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = '/membership/overview';
      break;
    }
    case 'account_prior_deletion_notice': {
      message =
        'Your Account is Scheduled for Deletion in 7 Days - Update Payment Method Required';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = isSuperAdmin ? '/membership' : '/dashboard';
      break;
    }
    case 'monthly_overlimit_charge_success': {
      message = 'Payment for Overlimit Successful!';
      icon = 'check_circle';
      iconColor = 'notification__icon--success';
      link = '/membership/overview';
      break;
    }
    case 'near_verification_limit_enterprise': {
      message =
        'Your Company Credits are Running Low! Overlimit fees apply per verification';
      icon = 'warning';
      iconColor = 'notification__icon--warning';
      link = '/membership/overview';
      break;
    }
    case 'customer_request_approved':
    case 'subscription_request_approved': {
      const {cr_id} = details;
      message = 'Enterprise Subscription Request Approved!';
      icon = 'check_circle';
      iconColor = 'notification__icon--success';
      link = `/enquiries/subscription/${cr_id}`;
      break;
    }
    case 'subscription_request_declined': {
      const {cr_id} = details;
      message = 'Enterprise Subscription Request Declined!';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = `/enquiries/subscription/${cr_id}`;
      break;
    }
    case 'subscription_request_cancelled': {
      const {cr_id} = details;
      message = 'Enterprise Subscription Request Cancelled!';
      icon = 'remove_circle';
      iconColor = 'notification__icon--disabled';
      link = `/enquiries/subscription/${cr_id}`;
      break;
    }
    case 'customer_request_under_review': {
      message = 'Contact Request Sent Successfully!';
      icon = 'check_circle';
      iconColor = 'notification__icon--success';
      link = `/enquiries/subscription/status`;
      break;
    }
    case 'subscription_success': {
      const {plan} = details;

      icon = 'check_circle';
      link = '/membership/overview';
      iconColor = 'notification__icon--success';

      if (!plan) {
        const freePlan =
          SUBSCRIPTION_TYPE_LABEL[SUBSCRIPTION_TYPE_LEVEL['free']];
        message = `Subscription to ${freePlan} Plan Successful!`;
      } else if (plan === 'trialing') {
        message = 'Subscription Upgrade to Free Trial Plan Successful!';
      } else {
        const upgradedPlan =
          SUBSCRIPTION_TYPE_LABEL[SUBSCRIPTION_TYPE_LEVEL[plan]];
        message = `Subscription Upgrade to ${upgradedPlan} Plan Successful!`;
      }
      break;
    }
    case 'subscription_payment_confirmation': {
      const {plan} = details || {};
      const upgradedPlan =
        SUBSCRIPTION_TYPE_LABEL[SUBSCRIPTION_TYPE_LEVEL[plan]];

      if (plan === 'trialing') {
        message = `Subscription Upgrade to Free Trial Plan Successful!`;
        icon = 'check_circle';
        iconColor = 'notification__icon--success';
        link = `/membership/overview`;
        break;
      }

      message = `Subscription Upgrade to ${upgradedPlan} Plan Successful!`;
      icon = 'check_circle';
      iconColor = 'notification__icon--success';
      link = `/membership/overview`;
      break;
    }
    case 'subscription_free_trial_ended_basic':
    case 'subscription_upgrade_confirmation': {
      const {plan} = details || {};
      message = `Subscription Upgrade to ${SUBSCRIPTION_PLANS_TYPE_LABEL[plan]} Plan Successful!`;
      icon = 'check_circle';
      iconColor = 'notification__icon--success';
      link = `/membership/overview`;
      break;
    }
    case 'subscription_payment_confirmation_as': {
      message = 'Adding of User Seat Successful';
      icon = 'check_circle';
      iconColor = 'notification__icon--success';
      link = `/users`;
      break;
    }
    case 'near_verification_limit': {
      message = `Your ${startCase(verification_type)} Credits are Running Low!`;
      icon = 'warning';
      iconColor = 'notification__icon--warning';
      link = isSuperAdmin ? '/membership' : '/dashboard';
      break;
    }
    case 'out_of_credit': {
      // v1
      message = `Out of ${startCase(verification_type)} Credit!`;
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = isAdminOrStaff ? '/dashboard' : '/membership/overview';
      break;
    }
    case 'out_of_credit_version_2': {
      // v2
      message = `You're out of credits`;
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = isAdminOrStaff ? '/dashboard' : '/membership/overview';
      break;
    }
    case 'subscription_payment_failed': {
      message = 'Subscription Payment Failed';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = `/membership/overview`;
      break;
    }
    case 'subscription_payment_reminder': {
      message = 'Your payment method is about to expire';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = `/membership/overview`;
      break;
    }
    case 'data_import_upload_success': {
      message = 'Bulk Import Completed Successfully';
      icon = 'check_circle';
      iconColor = 'notification__icon--success';
      link = `/import-management/activities`;
      break;
    }
    case 'data_import_upload_error': {
      message = 'Bulk Import Process Failed';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = `/import-management/activities`;
      break;
    }
    case 'data_import_upload_partial': {
      message = 'Bulk Import Partially Successful';
      icon = 'warning';
      iconColor = 'notification__icon--warning';
      link = `/import-management/activities`;
      break;
    }
    case 'verification_grace_period_nearing_expiration': {
      const {company_name, cert_number, verification_expiry_at} = details || {};
      message = (
        <>
          You are about to lose access to {company_name} - {cert_number} on{' '}
          <strong>{dayjs(verification_expiry_at).format('YYYY-MM-DD')}</strong>
        </>
      );
      icon = 'warning';
      iconColor = 'notification__icon--warning';
      link = `/certifications-list`;
      break;
    }
    case 'verification_grace_period_nearing_expiration_today': {
      const {company_name, cert_number} = details || {};
      message = (
        <>
          You are about to lose access to {company_name} - {cert_number}{' '}
          <strong>today</strong>
        </>
      );
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = `/certifications-list`;
      break;
    }
    case 'verification_grace_period_expired': {
      const {company_name, cert_number} = details || {};
      message = `You lose access to ${company_name} - ${cert_number}`;
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = `/certifications-list`;
      break;
    }
    case 'watchlist_certifications_update': {
      const {number_of_updated_certs} = details || {};

      const label =
        number_of_updated_certs > 1
          ? 'Certifications have been updated'
          : 'Certification has been updated';

      message = `${number_of_updated_certs} ${label}`;
      icon = 'warning';
      iconColor = 'notification__icon--success';
      link = `/watch-list`;
      break;
    }
    case 'import_status_update': {
      const {status_code} = details || {};

      switch (status_code) {
        case ACTIVITY_RESULT_STATUS.success:
          message = 'Bulk Import Completed Successfully';
          icon = 'check_circle';
          iconColor = `notification__icon--success`;
          break;
        case ACTIVITY_RESULT_STATUS.error:
          message = 'Bulk Import Process Failed';
          icon = 'warning';
          iconColor = `notification__icon--error`;
          break;
        case ACTIVITY_RESULT_STATUS.partially_verified:
          message = 'Bulk Import Partially Successful';
          icon = 'warning';
          iconColor = `notification__icon--warning`;
          break;
        default:
          break;
      }

      link = `/import-management/activities`;
      break;
    }

    case 'subscription_free_trial_ending': {
      message = 'Your free trial is ending in 3 days';
      icon = 'warning';
      iconColor = `notification__icon--warning`;
      link = `/membership/overview`;
      break;
    }
    case 'subscription_free_trial_ended_free': {
      message = 'Subscription Payment Failed, Reverted to Free Plan';
      icon = 'warning';
      iconColor = `notification__icon--error`;
      link = `/membership/overview`;
      break;
    }

    case 'subscription_free_trial_canceled_free': {
      message = 'Free Trial Cancellation Successful!';
      icon = 'check_circle';
      iconColor = `notification__icon--success`;
      link = `/membership/overview`;
      break;
    }

    case 'batch_error': {
      const {dm_upload_batch_id} = details;

      message = 'Certification data upload failed';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = `/import-management/activities/${dm_upload_batch_id}`;
      break;
    }
    case 'batch_validated': {
      const {dm_upload_batch_id} = details;

      message = 'Certification data has been validated';
      icon = 'check_box';
      iconColor = 'notification__icon--success';
      link = `/import-management/activities/${dm_upload_batch_id}`;
      break;
    }
    case 'batch_partially_imported': {
      const {dm_upload_batch_id} = details;

      message = 'Certification data has been partially imported';
      icon = 'check_box';
      iconColor = 'notification__icon--warning';
      link = `/import-management/activities/${dm_upload_batch_id}`;
      break;
    }
    case 'batch_ftp_error': {
      const {dm_upload_batch_id} = details;

      message = 'Certification data upload failed';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = `/import-management/activities/${dm_upload_batch_id}`;
      break;
    }
    // BE should not send this code, this should be only for the old entries of this code that is unread
    case 'batch_ftp_validated': {
      const {dm_upload_batch_id} = details;

      message = (
        <>
          Import using FTP Server <b className="text-success">validated</b>
        </>
      );
      icon = 'check_box';
      iconColor = 'notification__icon--success';
      link = `/import-management/activities/${dm_upload_batch_id}`;
      break;
    }
    case 'batch_ftp_partially_imported': {
      const {dm_upload_batch_id} = details;

      message = 'Certification data has been partially imported';
      icon = 'check_box';
      iconColor = 'notification__icon--warning';
      link = `/import-management/activities/${dm_upload_batch_id}`;
      break;
    }
    // BE should not send this code, this should be only for the old entries of this code that is unread
    case 'file_upload_success':
      if (isEmpty(details) || !is_cert_upload) {
        message = (
          <>
            Certification Body Data has been{' '}
            <b className="text-primary">uploaded</b>
          </>
        );
        link = '/cb-upload/activity';
      } else {
        message = `${file_name} - ${uploaded_cert_count} certifications has been successfully uploaded`;
        link = null;
      }
      icon = 'check_box';
      iconColor = 'notification__icon--success';
      break;
    case 'file_upload_error':
      if (isEmpty(details) || !is_cert_upload) {
        message = 'Certification Body data upload failed';
        link = '/cb-upload/activity';
      } else {
        // BE should not send this scenario this should be only for the old entries of this code that is unread
        message = `${file_name} - Failed to upload all certifications. ${uploaded_cert_count} uploaded; ${skipped_cert_count} skipped with ${error_count} errors`;
        link = null;
      }
      icon = 'warning';
      iconColor = 'notification__icon--error';
      break;
    // BE should not send this code this should be only for the old entries of this code that is unread
    case 'ftp_file_upload_success':
      if (isEmpty(details) || !is_cert_upload) {
        message = (
          <>
            Certification Body Data has been{' '}
            <b className="text-primary">fetched</b>
          </>
        );
        link = '/cb-upload/activity';
      } else {
        message = `${file_name} - ${uploaded_cert_count} certifications has been successfully uploaded through FTP`;
        link = null;
      }
      icon = 'check_box';
      iconColor = 'notification__icon--success';
      break;
    case 'ftp_file_upload_error':
      if (isEmpty(details) || !is_cert_upload) {
        message = 'Certification Body data upload failed';
        link = '/cb-upload/activity';
      } else {
        // BE should not send this scenario this should be only for the old entries of this code that is unread
        message = `${file_name} - Failed to upload all certifications through FTP. ${uploaded_cert_count} uploaded; ${skipped_cert_count} skipped with ${error_count} errors`;
        link = null;
      }
      icon = 'warning';
      iconColor = 'notification__icon--error';
      break;
    case 'two_fa_enabled':
      icon = 'lock_outline';
      iconColor = 'notification__icon--enabled';
      message = '2FA Authentication is enabled.';
      link = '/settings/security';
      break;
    case 'two_fa_disabled':
      icon = 'lock_open';
      iconColor = 'notification__icon--disabled';
      message = '2FA Authentication is disabled.';
      link = '/settings/security';
      break;
    case 'update_password':
      icon = 'key';
      iconColor = 'notification__icon--password';
      message = 'Password has been updated.';
      link = '/settings/security';
      break;
    case 'certificate_update':
      icon = 'folder_open';
      iconColor = 'notification__icon--cert';
      message = `Certification has been updated`;
      link = '/certifications-list';
      break;
    case 'watchlist_certificate_update':
      icon = 'folder_open';
      iconColor = 'notification__icon--cert';
      message = 'Watched Certificate has been updated';
      link = '/watch-list';
      break;
    case 'company_updated':
      icon = 'apartment';
      iconColor = 'notification__icon--company';
      message = 'Update Company';
      link = '/';
      break;
    case 'enquiry':
      icon = 'email';
      iconColor = 'notification__icon--enquiry';
      message = 'New message on your enquiry';
      link = '/enquiries';
      break;
    case 'enquiry_reply':
      icon = 'email';
      iconColor = 'notification__icon--enquiry';
      message = 'New reply on your enquiry';
      link = '/enquiries';
      break;

    case 'auto_renewal_disabled': {
      const formatDate = (resetDate, format = 'MMMM D, YYYY') =>
        date(resetDate, format);
      message = `${plan_name} Plan will expire after ${formatDate(
        billing_date,
        'MMMM D, YYYY'
      )}`;
      icon = 'warning';
      iconColor = `notification__icon--warning`;
      link = isAdminOrStaff ? '/dashboard' : `/membership/overview`;
      break;
    }
    case 'auto_renewal_enabled': {
      const formatDate = (resetDate, format = 'MMMM D, YYYY') =>
        date(resetDate, format);
      message = `${plan_name} Plan will renew on ${formatDate(
        billing_date,
        'MMMM D, YYYY'
      )}`;
      icon = 'warning';
      iconColor = 'notification__icon--warning';
      link = `/membership/overview`;
      break;
    }
    case 'near_verification_limit_version_2_overlimit': {
      message =
        'Your Credits are Running Low! Overlimit fees apply per verification';
      icon = 'warning';
      iconColor = 'notification__icon--warning';
      link = isAdminOrStaff ? '/dashboard' : '/membership/overview';
      break;
    }
    case 'subscription_market_intelligence_success': {
      message = isAdminOrStaff
        ? 'Market Intelligence has been successfully added!'
        : 'You have successfully added Market Intelligence!';
      icon = 'check_circle';
      iconColor = 'notification__icon--success';
      link = '/analytics/certifications';
      break;
    }
    case 'renewal_initial_payment_failed': {
      message = `Payment for ${startCase(plan)} Plan Renewal Unsuccessful`;
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = isAdminOrStaff ? null : '/membership/overview';
      break;
    }
    case 'renewal_first_retry_payment_failed': {
      message =
        'Payment Unsuccessful - First Attempt: Update Payment Method Required';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = isAdminOrStaff ? null : '/membership/overview';
      break;
    }
    case 'renewal_second_retry_payment_failed': {
      message =
        'Payment Unsuccessful - Second Attempt: Update Payment Method Required';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = isAdminOrStaff ? null : '/membership/overview';
      break;
    }
    case 'subscription_expiring': {
      message = `${plan_name} Plan expires in 3 days`;
      icon = 'warning';
      iconColor = `notification__icon--warning`;
      link = isAdminOrStaff ? '/dashboard' : `/membership/overview`;
      break;
    }
    case 'subscription_downgraded_payment_failed': {
      message = 'Account Reverted to Free Plan';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = isAdminOrStaff ? null : '/membership/overview';
      break;
    }
    case 'subscription_ended': {
      message = 'Account Reverted to Free Plan';
      icon = 'warning';
      iconColor = 'notification__icon--error';
      link = isAdminOrStaff ? null : '/membership/overview';
      break;
    }
    case 'subscription_renewal_successful': {
      message = `${capitalize(plan)} Plan Renewal Successful`;
      icon = 'check_circle';
      iconColor = 'notification__icon--success';
      link = isAdminOrStaff ? '/dashboard' : '/membership/overview';
      break;
    }

    default:
      break;
  }

  return {
    message: message,
    link: link,
    iconColor: iconColor,
    icon: icon,
  };
}

function NotificationItem({
  notification,
  onClick,
  onMarkRead,
  onDelete,
  accountRole,
}) {
  const {code, status, created_at, details} = notification;
  const {message, link, iconColor, icon} = getNotificationProperties(
    code,
    details,
    accountRole
  );

  const className = classNames(styles['notifications__item'], {
    [styles['notifications__item--read']]: status === 'read',
    [styles['notifications__item--unread']]: status === 'unread',
  });

  return (
    <div className={className}>
      <div className="my-4">
        <MuiIcon
          name={icon}
          size="lg"
          className={classNames(
            styles['notification__icon'],
            styles[iconColor]
          )}
        />
      </div>
      <div
        className={styles['notifications__item--content']}
        onClick={() => onClick(link)}
      >
        <Badge className={styles['notifications__item--content--badge']}>
          {NOTIFICATION_CATEGORY_LABEL[code]}
        </Badge>
        <div>
          <p className={styles['notifications__item--content--message']}>
            {message}
          </p>
        </div>
        <span
          className={classNames(
            'text-muted',
            styles['notifications__item--content--date']
          )}
        >
          <LabelDate timestamp={created_at} format="fromNow" />
        </span>
      </div>
      <div className={styles['notifications__item--actions']}>
        <NotificationDropItem
          notification={notification}
          onMarkRead={onMarkRead}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
}

function PrivateNavNotification() {
  const account = useAuth();
  const accountRole = get(account, 'account.account_role');

  return (
    <NotificationProvider>
      <NotificationConsumer>
        {({
          isOpen,
          isFetching,
          isFetchingMore,
          data,
          count,
          nextKey,
          hasUnreadNotifications,
          handleFetchNotifications,
          handleClick,
          handleToggle,
          handleDelete,
          handleMarkAsRead,
          handleMarkAllAsRead,
        }) => {
          return (
            <Dropdown isOpen={isOpen} toggle={handleToggle} nav>
              <DropdownToggle
                nav
                color="transparent"
                className="position-relative mr-3"
              >
                <MuiIcon
                  name="notifications"
                  variant="outlined"
                  id="notification-bell"
                  className="text-body"
                  style={{fontSize: '1.25rem'}}
                />
                {count > 0 && (
                  <span className={styles['notifications__badge']}>
                    {count > 99 ? '99+' : count}
                  </span>
                )}
                <UncontrolledTooltip target="notification-bell">
                  Notifications
                </UncontrolledTooltip>
              </DropdownToggle>
              <DropdownMenu right className={styles['notifications__list']}>
                <DropdownItem header className="p-0">
                  <ContentTitle
                    className="py-3"
                    title={
                      <span className="mb-0 h5 flex-grow-1">Notifications</span>
                    }
                  >
                    <NotificationDropAll
                      toggle={handleToggle}
                      handleMarkAllAsRead={handleMarkAllAsRead}
                      hasUnreadNotifications={hasUnreadNotifications}
                    />
                  </ContentTitle>
                </DropdownItem>

                {isFetching && (
                  <div className="d-flex align-items-center justify-content-center p-3 h-100">
                    <LoadingSpinner isLoading />
                  </div>
                )}

                {!isFetching && isEmpty(data) && (
                  <div className={styles['notifications__item--empty']}>
                    <div className="mb-1 d-flex align-items-center justify-content-center">
                      <MuiIcon name="inbox" className="mr-1" /> No messages
                    </div>

                    <Link to="/settings/notification">
                      Your notification preferences..
                    </Link>
                  </div>
                )}

                <div style={{overflowY: 'auto', maxHeight: '300px'}}>
                  {!isFetching &&
                    !isEmpty(data) &&
                    data.map((item, index) => {
                      return (
                        <NotificationItem
                          key={index}
                          notification={item}
                          onClick={(link) =>
                            handleClick(item.status, item.notification_id, link)
                          }
                          onMarkRead={handleMarkAsRead}
                          onDelete={handleDelete}
                          accountRole={accountRole}
                        />
                      );
                    })}
                </div>

                {!isFetching && nextKey && (
                  <Button
                    color="link"
                    onClick={handleFetchNotifications}
                    block
                    disabled={isFetchingMore}
                  >
                    Load more
                  </Button>
                )}
              </DropdownMenu>
            </Dropdown>
          );
        }}
      </NotificationConsumer>
    </NotificationProvider>
  );
}

export default PrivateNavNotification;
